import React from 'react';
import { SummaryType } from '../../../../common/commonTypes';
import { pieArcLabelClasses, PieChart } from '@mui/x-charts/PieChart';
import { COLORS } from '../../../../common/colors';
import { styled } from '@mui/material/styles';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { useTranslation } from 'react-i18next';

type CalDistrChartType = {
    goals: SummaryType;
    dailySummary: SummaryType;
};

const CalDistrGoalsChart2 = (props: CalDistrChartType) => {
    const { goals, dailySummary } = props;

    const { t, i18n } = useTranslation();

    // TODO: think about abstraction to a separate component
    const CALORIES_PER_GRAM = {
        protein: 4,
        fats: 9,
        carbsNoSugars: 4,
        sugars: 4,
    };

    const calorieDistributionToday = {
        protein: dailySummary.protein * CALORIES_PER_GRAM.protein,
        fats: dailySummary.fats * CALORIES_PER_GRAM.fats,
        carbs: dailySummary.carbsNoSugars * CALORIES_PER_GRAM.carbsNoSugars,
        sugars: dailySummary.sugars * CALORIES_PER_GRAM.sugars,
    };

    const calorieDistributionWeeklyAvg = {
        protein: goals.protein * CALORIES_PER_GRAM.protein,
        fats: goals.fats * CALORIES_PER_GRAM.fats,
        carbs: goals.carbsNoSugars * CALORIES_PER_GRAM.carbsNoSugars,
        sugars: goals.sugars * CALORIES_PER_GRAM.sugars,
    };

    const totalCaloriesToday =
        calorieDistributionToday.protein +
        calorieDistributionToday.fats +
        calorieDistributionToday.carbs;

    const totalCaloriesWeeklyAvg =
        calorieDistributionWeeklyAvg.protein +
        calorieDistributionWeeklyAvg.fats +
        calorieDistributionWeeklyAvg.carbs;

    const pieDataToday = [
        {
            id: 'ProteinToday',
            label: t('protein'),
            value: calorieDistributionToday.protein,
            color: COLORS.protein,
        },
        {
            id: 'FatsToday',
            label: t('fat'),
            value: calorieDistributionToday.fats,
            color: COLORS.fat,
        },
        {
            id: 'CarbsToday',
            label: t('carbs'),
            value: calorieDistributionToday.carbs,
            color: COLORS.carb,
        },
        {
            id: 'SugarsToday',
            label: t('sugar'),
            value: calorieDistributionToday.sugars,
            color: COLORS.sugar,
        },
    ];

    const pieDataWeeklyAvg = [
        {
            id: 'ProteinWeekly',
            label: t('protein'),
            value: calorieDistributionWeeklyAvg.protein,
            color: COLORS.proteinLight,
        },
        {
            id: 'FatsWeekly',
            label: t('fat'),
            value: calorieDistributionWeeklyAvg.fats,
            color: COLORS.fatLight,
        },
        {
            id: 'CarbsWeekly',
            label: t('carbs'),
            value: calorieDistributionWeeklyAvg.carbs,
            color: COLORS.carbLight,
        },
        {
            id: 'SugarsWeekly',
            label: t('sugar'),
            value: calorieDistributionWeeklyAvg.sugars,
            color: COLORS.sugarLight,
        },
    ];

    const getArcLabelToday = (params: any) => {
        const percent = params.value / totalCaloriesToday;
        return `${(percent * 100).toFixed(0)}%`;
    };

    const getArcLabelWeekly = (params: any) => {
        const percent = params.value / totalCaloriesWeeklyAvg;
        return `${(percent * 100).toFixed(0)}%`;
    };

    return (
        <div className="flex w-full">
            <div className="w-1/2">
                {!totalCaloriesToday ? (
                    <div className="text-xs mt-20 text-gray-400 text-left">{t('no_data_yet')}</div>
                ) : (
                    <PieChart
                        series={[
                            {
                                startAngle: -180,
                                endAngle: 0,
                                data: pieDataToday.reverse(),
                                arcLabelMinAngle: 35,
                                arcLabel: getArcLabelToday,
                                valueFormatter: (v) => `${v.value} cal`,
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 11,
                            },
                        }}
                        margin={{ top: 0, bottom: 0, left: 80, right: -65 }}
                        slotProps={{
                            legend: {
                                labelStyle: { fontSize: 12 },
                                itemMarkWidth: 15,
                                itemMarkHeight: 15,
                                padding: 0,
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'left' },
                            },
                        }}
                        height={200}
                    />
                )}
            </div>
            <div className="w-1/2">
                {!totalCaloriesWeeklyAvg ? (
                    <div className="text-xs mt-20 text-gray-400 text-right">{t('no_data_yet')}</div>
                ) : (
                    <PieChart
                        series={[
                            {
                                startAngle: 0,
                                endAngle: 180,
                                data: pieDataWeeklyAvg,
                                arcLabelMinAngle: 35,
                                arcLabel: getArcLabelWeekly,
                                valueFormatter: (v) => `${v.value} cal`,
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 11,
                            },
                        }}
                        margin={{ top: 0, bottom: 0, left: -65, right: 80 }}
                        slotProps={{
                            legend: {
                                labelStyle: { fontSize: 12 },
                                itemMarkWidth: 15,
                                itemMarkHeight: 15,
                                padding: 0,
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'right' },
                            },
                        }}
                        height={200}
                    />
                )}
            </div>
        </div>
    );
};

export default CalDistrGoalsChart2;
